/**
 * MSAL Authentication Configuration for Mobile-Staff App
 *
 * Microsoft-only SSO using Azure AD / Entra ID.
 * No Google GIS or Apple Sign In — this is a simplified auth setup.
 *
 * Values are sourced from environment files selected at build time
 * via Angular's fileReplacements mechanism.
 */
import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../../environments/environment';

/** MSAL scopes requested during login (Microsoft Graph default). */
const MSAL_SCOPES = ['user.read'];

/** API scope for backend calls (custom scope from Azure AD app registration). */
const API_SCOPE = `api://${environment.msal.clientId}/.default`;

/**
 * MSAL Logger Callback
 *
 * Handles logging output from MSAL. Enable verbose logging in dev by
 * uncommenting the console.log line.
 */
function loggerCallback(logLevel: LogLevel, message: string): void {
  // Uncomment for debugging MSAL authentication issues:
  // console.log(`MSAL [${LogLevel[logLevel]}]:`, message);
}

/**
 * MSAL Instance Factory
 *
 * Creates the PublicClientApplication singleton used by MsalService.
 * Uses redirect flow (not popup) for mobile-friendly auth.
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: environment.msal.authority,
      redirectUri: environment.msal.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

/**
 * MSAL Guard Configuration Factory
 *
 * Configures the MsalGuard for route protection.
 * Uses redirect interaction type — suitable for mobile browsers.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: MSAL_SCOPES,
    },
    loginFailedRoute: '/login',
  };
}

/**
 * MSAL Interceptor Configuration Factory
 *
 * Configures automatic token injection for outgoing HTTP requests.
 * Maps API endpoints to their required scopes so the interceptor
 * can acquire and attach tokens transparently.
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  // Microsoft Graph API
  protectedResourceMap.set('https://graph.microsoft.com/*', MSAL_SCOPES);

  // Backend API endpoints — all GTLC API calls get the API scope
  protectedResourceMap.set(environment.apiUrl, [API_SCOPE]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/** Re-export scopes and API scope for use in services/guards. */
export { MSAL_SCOPES, API_SCOPE };